import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@auth/AuthContext";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Autocomplete,
  Chip,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PoLineAuditTable from "./PoLineAuditTable";
import { ArrowBack, AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import apiClient from "@utils/axiosInstance";
import { fontSizes, letterSpacings, lineHeights, shadow } from "../../../design-system/tokens";
import { FileUploadZone } from "@modules/invoice/components/FileUploadZone";
import { FileAttachmentList } from "@modules/invoice/components/FileAttachmentList";
import { useConfirm } from "@contexts/ConfirmDialogContext";
import RelatedPosBulkActionDialog from "@components/RelatedPosBulkActionDialog";
import { useSnackbar } from "@contexts/SnackbarContext";


const emptyForm = {
  poNo: "",
  releaseNo: "",
  lineNo: "",
  shipmentNo: "",
  distribution: "",
  item: "",
  itemDescription: "",
  quantity: "",
  quantityShipped: "",
  unitValue: "",
  extendedValue: "",
  // LWI-854 — currency code (USD/CAD/etc), shown as adornment on Extended Value
  // when the user has `lcs_po.view_currency`. Sourced from po_lines.monetary_currency.
  monetaryCurrency: "",
  poLineStatus: "",
  shipToLocation: "",
  promiseDate: "",
  needByDate: "",
  supplierNo: "",
  supplierName: "",
  buyer: "",
  programEndUse: "",
  project: "",
  // LWI-560 — admin-only Task field, read-only, sourced from po_master.task.
  task: "",
  usml: "",
  eccn: "",
  authorizationRequirement: "",
  eclGoods: "",
  eccnEarTechnology: "",
  usmlItarTechData: "",
  eclTechnology: "",
  canadianControlledGoods: "",
  lastTradeComplianceReviewDate: "",
  ecrComplianceReviewDate: "",
  usHsCode: "",
  caHsCode: "",
  supplierCountry: "",
  hasUsContent: null,
  comments: "",
  authorizationNumber: "",
  authorizationStatus: "",
  authorizationLine: "",
  notesDateTime: "",
  notes: "",
  // LWI-704 / LWI-734 — admin-edit, supplier-visible.
  adminComments: "",
  // LWI-842 — source provenance, surfaced as header subtext. Null for IPR.
  oracleCreateDate: "",
  filename: "",
};

// Parse a composite po_id like "35272100,1248,11,47588,1" into its parts.
const parsePOId = (poId = "") => {
  const parts = poId.split(",");
  return {
    poNo:         parts[0] || "",
    releaseNo:    parts[1] || "",
    lineNo:       parts[2] || "",
    shipmentNo:   parts[3] || "",
    distribution: parts[4] || "",
  };
};

export const EditPurchaseOrderPage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { poNumber } = useParams();
  const { hasPerm } = useAuth();
  const [searchParams] = useSearchParams();
  const region = searchParams.get("region") || "USA";
  // LWI-661 — gates pulled from the catalog. lcs_po.view.any is the
  // admin-tier flag used throughout this page. The legacy `isInternal`
  // (admin OR owner) collapses into the same flag because `owner` was
  // confirmed dead-code role-string during the cutover.
  const isAdmin = hasPerm("lcs_po.view.any");
  const isInternal = isAdmin;
  // LWI-824 — PO-derived fields the supplier can see but must not edit.
  // The PO list ships them as the source of truth (Oracle import); supplier
  // edits were a side door that let them drift. Admins are unaffected.
  const supplierReadOnly = !isInternal;
  const confirm = useConfirm();
  const pid = searchParams.get("pid");
  // LWI-1150 — Details/Audit tab split. The Audit tab (authorization audit
  // trail) only exists for holders of lcs_po.audit.view; everyone else gets
  // the page exactly as before, no tab bar.
  const canViewAudit = hasPerm("lcs_po.audit.view");
  const [activeTab, setActiveTab] = useState("details");

  const { poNo: basePONo } = parsePOId(poNumber);

  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // LWI-855 — importer audit trail for this PO line. Only fetched when the
  // caller has `lcs_po.view_import_history`; the BE returns 403 otherwise.
  const [importHistory, setImportHistory] = useState([]);
  const [importHistoryLoading, setImportHistoryLoading] = useState(false);
  // LWI-557 / LWI-558 — optional override for the success Alert text so the
  // bulk paths can replace the default "Changes saved successfully." copy.
  const [successMessage, setSuccessMessage] = useState(null);
  // LWI-557 / LWI-558 — shared bulk action dialog state.
  // bulkDialogMode is null when closed; "us_content" or "authorization" when open.
  const [bulkDialogMode, setBulkDialogMode] = useState(null);
  // LWI-823 — set when a successful save should pop the user back to the PO
  // list. We defer the navigation until any bulk-apply dialog spawned by
  // the save has been closed (so the user can interact with it first).
  const navigateAfterBulkCloseRef = useRef(false);
  const [bulkCandidates, setBulkCandidates] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [licenseNumbers, setLicenseNumbers] = useState([]);
  const [licenseNumbersLoading, setLicenseNumbersLoading] = useState(false);
  // Raw program_name resolved server-side from po_master/po_lines.project ->
  // lcs_projects -> lcs_programs. Used to filter the license Autocomplete to
  // licenses whose program matches the PO's program (LWI-534). Null when the
  // project is not mapped in lcs_projects — in that case the filter is off.
  const [projectProgramName, setProjectProgramName] = useState(null);
  const [programOptions, setProgramOptions] = useState([]);
  // LWI-535 — authoritative list of {licenseNumber, authorizationLine} rows
  // on this PO line. Each row also caches its own lineOptions + linesLoading
  // so the per-row Authorization Line Autocomplete has its own options.
  // Replaces the scalar formData.authorizationNumber/authorizationLine used
  // before; those are still in formData and updated to mirror row 0 for
  // reset-detection back-compat.
  const [authorizationRows, setAuthorizationRows] = useState([]);
  const [exemptionOptions, setExemptionOptions] = useState([]);
  // LWI-1164 — "Exceptions" subheader options (RPL / LVS), managed via the
  // authorization_exception dropdown field (Shipment Admin).
  const [exceptionOptions, setExceptionOptions] = useState([]);
  const [certAttachments, setCertAttachments] = useState([]);
  // Server-authoritative "authorized to ship" value. Updated on load, on
  // successful admin authorize, and on save when the backend's transition
  // rule fires. The chip itself is derived from this plus formData.hasUsContent
  // so that clicking No reflects instantly without a stale optimistic state.
  const [savedAuthorizedToShip, setSavedAuthorizedToShip] = useState(false);
  // LWI-1169 — server said EXPLICIT Not Authorized (authorized_to_ship = 0),
  // as opposed to unactioned (NULL). Distinct from savedAuthorizedToShip so
  // the chip can show "Not Authorized" even for no-US-content lines (which
  // otherwise derive as green permanently via the LWI-517 preview below).
  const [explicitlyNotAuthorized, setExplicitlyNotAuthorized] = useState(false);
  // LWI-800 — po_master.destination for the AB03 (Sumner) early-warning
  // banner. CAN-AB1 / CAN-AB3 are US-to-US delivery points; CCI submission
  // on those is suspended downstream (LWI-768, LWI-799). Flag it up-front
  // so the supplier doesn't fill out an entire CCI before learning that.
  // LWI-1102 — only a US-to-US movement when the supplier is also US, so
  // the banner additionally requires isUsaSupplier (defined below).
  const [destinationCode, setDestinationCode] = useState("");
  const isUsToUsDestination = ["CAN-AB1", "CAN-AB3"].includes(
    (destinationCode || "").toUpperCase()
  );

  // LWI-715 — last-persisted has_us_content for this line, normalized to a
  // strict boolean. Still used by the LWI-714 reset flow and a few comment-
  // related effects below, even though it no longer drives the supplier-
  // edit lock (LWI-763 moved that gate onto authorized_to_ship).
  const originalHasUsContentRef = useRef(false);

  // LWI-715 → LWI-735 → LWI-763 — supplier-edit lock. Engages when the line
  // has actually been Authorized to Ship and the caller lacks bypass perms /
  // admin scope. Previous incarnations keyed off has_us_content === true
  // (LWI-715) and the Yes/No-button special case (LWI-735); per LWI-763 the
  // supplier should be able to iterate until the admin has approved the
  // shipment, so the single signal is the authorization itself.
  const isLocked =
    savedAuthorizedToShip === true &&
    !hasPerm("lcs_po.edit.locked") &&
    !isAdmin;

  // LWI-1075 — the US Content / US-authorization section applies to every
  // non-USA supplier, not just Canadian ones. International suppliers carry
  // the same license/authorization obligations, and the PO grid already
  // treats all non-USA tabs (CDN + INT) as authorization-relevant, so the
  // edit page must surface the same section+wording for them. USA suppliers
  // are excluded (US content certification isn't meaningful for a US origin).
  const supplierCountryUpper = (formData.supplierCountry || "").toUpperCase().trim();
  const isNonUsaSupplier =
    supplierCountryUpper !== "" &&
    supplierCountryUpper !== "USA" &&
    supplierCountryUpper !== "UNITED STATES";
  // LWI-1102 — explicit-US check (not just !isNonUsaSupplier: a blank /
  // unknown country must not trigger the US-to-US banner or suspension).
  const isUsaSupplier =
    supplierCountryUpper === "USA" || supplierCountryUpper === "UNITED STATES";

  // LWI-870 — secondary lock that engages once any CCI carrying this PO
  // line's license (via cci_licenses.source_po_line_pid) reaches APPROVED
  // or SUSPENDED. Scope is license-related only (Authorization Status, the
  // license-row Autocompletes, the Add/Remove license controls); the rest
  // of the supplier-editable LCS surface stays open. Admins with
  // lcs_po.edit.locked bypass — the BE drops the license writes silently
  // for everyone else and surfaces a cci_license_locked warning. The flag
  // is set from line.isLockedByCci in the load handlers below.
  const [isLockedByCci, setIsLockedByCci] = useState(false);
  const licenseFieldsLocked =
    isLocked ||
    (isLockedByCci && !hasPerm("lcs_po.edit.locked") && !isAdmin);

  // Snapshot of the USML/ECCN/programEndUse + auth requirement values received
  // from the server on the most recent load. Used to suppress the client-side
  // auth recompute (see the effect below LWI-516) when the user hasn't actually
  // changed any classification input — otherwise an empty or non-EN-dashed
  // programEndUse causes the frontend to silently overwrite the server's
  // correct value with "No License Required".
  const loadedInputsRef = useRef({
    usml: "",
    eccn: "",
    programEndUse: "",
    authorizationRequirement: "",
  });

  // Snapshot of authorization fields at load time. Used by handleSave to
  // detect when an admin has transitioned the status back to Outstanding
  // on a line that has authorization data or attachments worth warning
  // about — LWI-537.
  const initialAuthRef = useRef({
    authorizationStatus: "",
    authorizationNumber: "",
    authorizationLine: "",
    authorizedToShip: false,
  });

  useEffect(() => {
    const fetchPO = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(
          `/purchase-orders/${poNumber}`
        );
        const { po, lines } = response.data.data;

        const line = pid
          ? lines.find((l) => String(l.pid) === String(pid)) || lines[0]
          : lines[0];

        if (!line) {
          setError("No lines found for this purchase order.");
          return;
        }

        const parsed = parsePOId(po.poNumber);

        setFormData({
          poNo: parsed.poNo,
          releaseNo: line.releaseNumber || parsed.releaseNo,
          lineNo: line.lineNumber != null ? String(line.lineNumber) : parsed.lineNo,
          shipmentNo: line.shipmentNumber || parsed.shipmentNo,
          distribution: line.distribution || parsed.distribution,
          item: line.item || "",
          itemDescription: line.itemDescription || "",
          quantity: line.quantity != null ? String(line.quantity) : "",
          quantityShipped:
            line.quantityShipped != null ? String(line.quantityShipped) : "",
          unitValue: line.unitValue != null ? String(line.unitValue) : "",
          extendedValue: (line.unitValue != null && line.quantity != null)
            ? String((parseFloat(line.unitValue) * parseFloat(line.quantity)) || 0)
            : line.extendedValue != null ? String(line.extendedValue) : "",
          monetaryCurrency: line.monetaryCurrency || "",
          poLineStatus: line.poLineStatus || po.poLineStatus || "",
          shipToLocation: line.shipToLocation || po.shipToLocation || "",
          promiseDate: line.promiseDate
            ? line.promiseDate.substring(0, 10)
            : "",
          needByDate: line.needByDate
            ? line.needByDate.substring(0, 10)
            : "",
          supplierNo: po.supplierNo || "",
          supplierName: po.supplierName || "",
          buyer: po.buyerEmail || po.buyerName || "",
          programEndUse: line.programEndUse || "",
          project: po.project || "",
          task: po.task || "",
          usml: line.usml || "",
          eccn: line.eccn || "",
          authorizationRequirement: line.authorizationRequirement || "",
          eclGoods: line.eclGoods || "",
          eccnEarTechnology: line.eccnEarTechnology || "",
          usmlItarTechData: line.usmlItarTechData || "",
          eclTechnology: line.eclTechnology || "",
          canadianControlledGoods: line.canadianControlledGoods || "",
          lastTradeComplianceReviewDate: line.lastTradeComplianceReviewDate
            ? line.lastTradeComplianceReviewDate.substring(0, 10)
            : "",
          ecrComplianceReviewDate: line.ecrComplianceReviewDate
            ? line.ecrComplianceReviewDate.substring(0, 10)
            : "",
          usHsCode: line.usHsCode || "",
          caHsCode: line.caHsCode || "",
          supplierCountry: po.supplierCountry || "",
          // MySQL TINYINT(1) returns 0/1 (integers). Coerce to strict booleans
          // so button highlighting (=== true / === false) and chip derivation
          // work after a reload. Null/undefined stays null (not-yet-answered).
          hasUsContent:
            line.hasUsContent === null || line.hasUsContent === undefined
              ? null
              : !!Number(line.hasUsContent),
          comments: line.comments || "",
          authorizationNumber: line.authorizationNumber || "",
          authorizationStatus: line.authorizationStatus || "Outstanding",
          authorizationLine: line.authorizationLine || "",
          notesDateTime: line.updatedAt || "",
          notes: line.notes || "",
          // LWI-704 / LWI-734 — admin-edit, supplier-visible channel.
          adminComments: line.adminComments || "",
          // LWI-842 — provenance for the header subtext.
          oracleCreateDate: po.oracleCreateDate || "",
          filename: po.filename || "",
        });

        // LWI-715 — capture the last-persisted has_us_content as a strict
        // boolean so the lock derivation can use === true safely. NULL/0
        // both stay false; only TINYINT 1 becomes true.
        originalHasUsContentRef.current = !!Number(line.hasUsContent);

        setSavedAuthorizedToShip(!!line.authorizedToShip);
        // LWI-1169 — String() keeps an explicit 0 distinct from NULL (only 0
        // means an admin actively set Not Authorized).
        setExplicitlyNotAuthorized(String(line.authorizedToShip) === "0");
        // LWI-870 — capture whether the BE flagged this line as CCI-locked
        // (any APPROVED / SUSPENDED CCI references it via cci_licenses).
        // Drives the secondary license-fields lock + banner below.
        setIsLockedByCci(!!line.isLockedByCci);
        // LWI-800 — capture po_master.destination separately so the AB03
        // warning banner can fire deterministically. Falls back to
        // shipToLocation when the new BE field hasn't shipped yet.
        setDestinationCode(po.destination || po.shipToLocation || "");
        setProjectProgramName(line.projectProgramName || null);

        // LWI-535 — hydrate authorizationRows from the new line.authorizations
        // array. Falls back to the legacy scalar if the backend is pre-LWI-535
        // so the UI still works during the rollout gap.
        const incomingAuths = Array.isArray(line.authorizations) && line.authorizations.length > 0
          ? line.authorizations.map((a) => ({
              licenseNumber: a.licenseNumber || "",
              authorizationLine: a.authorizationLine || "",
              lineOptions: [],
              linesLoading: false,
            }))
          : line.authorizationNumber
          ? [{
              licenseNumber: line.authorizationNumber,
              authorizationLine: line.authorizationLine || "",
              lineOptions: [],
              linesLoading: false,
            }]
          : [];
        setAuthorizationRows(incomingAuths);

        // Record the server-authoritative classification values so the auth
        // recompute effect can tell "user-initiated change" apart from "fresh
        // page load" and avoid clobbering the backend-computed auth value.
        loadedInputsRef.current = {
          usml: line.usml || "",
          eccn: line.eccn || "",
          programEndUse: line.programEndUse || "",
          authorizationRequirement: line.authorizationRequirement || "",
        };

        initialAuthRef.current = {
          authorizationStatus: line.authorizationStatus || "Outstanding",
          authorizationNumber: line.authorizationNumber || "",
          authorizationLine: line.authorizationLine || "",
          authorizedToShip: !!line.authorizedToShip,
        };

        // Load US Content certification attachments for this PO line
        try {
          const attResp = await apiClient.get(
            `/purchase-orders/lines/${line.pid}/attachments`
          );
          const rows = attResp.data?.data || [];
          setCertAttachments(
            rows.map((r) => ({
              id: r.id,
              name: r.original_name,
              size: r.size_bytes,
              type: r.mime_type,
              uploadedAt: r.created_at,
            }))
          );
        } catch (attErr) {
          console.error("Error loading certification attachments:", attErr);
          setCertAttachments([]);
        }
      } catch (err) {
        console.error("Error fetching PO:", err);
        setError("Failed to load purchase order data.");
      } finally {
        setLoading(false);
      }
    };

    fetchPO();
  }, [poNumber, pid]);

  // Auto-compute authorization requirement based on USML, ECCN, and end-use country.
  // Skipped on initial page load when the server already provided a value AND the
  // classification inputs haven't changed — see LWI-516. Without this guard, an
  // empty or non-EN-dashed `programEndUse` would make the client recompute return
  // "No License Required" and overwrite a correct backend-computed auth.
  useEffect(() => {
    if (loading) return;
    const loaded = loadedInputsRef.current;
    if (
      loaded.authorizationRequirement &&
      formData.usml === loaded.usml &&
      formData.eccn === loaded.eccn &&
      formData.programEndUse === loaded.programEndUse
    ) {
      return;
    }
    const computeAuth = async () => {
      try {
        // Extract end-use country from programEndUse (format: "ProgramName – Country")
        const endUseCountry = formData.programEndUse
          ? formData.programEndUse.split("–").pop()?.trim() || ""
          : "";
        const response = await apiClient.get("/purchase-orders/authorization-check", {
          params: {
            usml: formData.usml || "",
            eccn: formData.eccn || "",
            country: endUseCountry || "",
          },
        });
        const computed = response.data?.authorizationRequirement || "No License Required";
        setFormData((prev) => ({ ...prev, authorizationRequirement: computed }));
      } catch (err) {
        console.error("Error computing authorization:", err);
      }
    };
    computeAuth();
  }, [formData.usml, formData.eccn, formData.programEndUse, loading]);

  // Fetch license numbers (backend filters by role: admin=all, non-admin=by supplier_id).
  // LWI-600 — `available=1` restricts to approved + non-expired licenses so the
  // picker only offers licenses the supplier can actually attach right now.
  useEffect(() => {
    const fetchLicenses = async () => {
      setLicenseNumbersLoading(true);
      try {
        const response = await apiClient.get("/licenses", {
          params: { available: 1 },
        });
        const data = response.data?.data || [];
        setLicenses(data);
        setLicenseNumbers(data.map((l) => l.licenseNumber).filter(Boolean));
      } catch (err) {
        console.error("Error fetching license numbers:", err);
        setLicenseNumbers([]);
      } finally {
        setLicenseNumbersLoading(false);
      }
    };
    fetchLicenses();
  }, []);

  // LWI-535 — fetch license lines for one authorization row and store them
  // on that row. Called when the user picks a license on a row, and on
  // initial load for each pre-populated row.
  const fetchRowLines = async (rowIndex, licenseNumber, licensesList) => {
    const license = (licensesList || licenses).find((l) => l.licenseNumber === licenseNumber);
    if (!license) return; // exemption string — no lines to fetch
    setAuthorizationRows((prev) =>
      prev.map((r, i) => (i === rowIndex ? { ...r, linesLoading: true } : r))
    );
    try {
      const res = await apiClient.get(`/licenses/${license.id}/lines`);
      const opts = (res.data?.data || [])
        .map((l) => String(l.lineNumber))
        .filter(Boolean);
      setAuthorizationRows((prev) =>
        prev.map((r, i) =>
          i === rowIndex ? { ...r, lineOptions: opts, linesLoading: false } : r
        )
      );
    } catch (err) {
      console.error("Error fetching license lines:", err);
      setAuthorizationRows((prev) =>
        prev.map((r, i) =>
          i === rowIndex ? { ...r, lineOptions: [], linesLoading: false } : r
        )
      );
    }
  };

  const addAuthRow = () => {
    setAuthorizationRows((prev) => [
      ...prev,
      { licenseNumber: "", authorizationLine: "", lineOptions: [], linesLoading: false },
    ]);
  };

  const removeAuthRow = (index) => {
    setAuthorizationRows((prev) => prev.filter((_, i) => i !== index));
  };

  const setAuthRowLicense = (index, value) => {
    setAuthorizationRows((prev) =>
      prev.map((r, i) =>
        i === index
          ? { ...r, licenseNumber: value, authorizationLine: "", lineOptions: [], linesLoading: false }
          : r
      )
    );
    if (value) fetchRowLines(index, value);
  };

  const setAuthRowLine = (index, value) => {
    setAuthorizationRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, authorizationLine: value } : r))
    );
  };

  // When the license fetch finishes, back-fill line options for rows that
  // were pre-populated on load. Runs once both licenses and rows are ready.
  const linesPrefetched = useRef(false);
  useEffect(() => {
    if (linesPrefetched.current) return;
    if (loading || licenseNumbersLoading) return;
    if (licenses.length === 0 || authorizationRows.length === 0) return;
    linesPrefetched.current = true;
    authorizationRows.forEach((row, idx) => {
      if (row.licenseNumber && row.lineOptions.length === 0) {
        fetchRowLines(idx, row.licenseNumber, licenses);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, licenseNumbersLoading, licenses, authorizationRows]);

  useEffect(() => {
    // Reset the prefetch guard whenever the user navigates to a different PO line.
    linesPrefetched.current = false;
  }, [poNumber, pid]);

  // Fetch ITAR exemptions for authorization dropdown
  useEffect(() => {
    apiClient.get("/dropdown_fields/itar_exemption/values")
      .then((res) => {
        const vals = (res.data || []).map((e) => e.value).filter(Boolean);
        setExemptionOptions(vals);
      })
      .catch(() => setExemptionOptions([]));
  }, []);

  // LWI-1164 — fetch the "Exceptions" options (RPL / LVS) for the dropdown.
  useEffect(() => {
    apiClient.get("/dropdown_fields/authorization_exception/values")
      .then((res) => {
        const vals = (res.data || []).map((e) => e.value).filter(Boolean);
        setExceptionOptions(vals);
      })
      .catch(() => setExceptionOptions([]));
  }, []);

  // Fetch program-end-use options from lcs_programs
  useEffect(() => {
    apiClient.get("/lcs-programs")
      .then((res) => {
        const programs = res.data?.data || [];
        const options = programs
          .filter((p) => !p.deleted)
          .map((p) => {
            const label = [p.program_name, p.country_end_use].filter(Boolean).join(" – ");
            return label;
          })
          .filter(Boolean);
        setProgramOptions(options);
      })
      .catch(() => setProgramOptions([]));
  }, []);

  // LWI-855 — fetch import history once we know the pid and the caller has
  // the perm. BE enforces the gate too; this short-circuit avoids a known-403.
  useEffect(() => {
    if (!pid || !hasPerm("lcs_po.view_import_history")) {
      setImportHistory([]);
      return;
    }
    let cancelled = false;
    setImportHistoryLoading(true);
    apiClient
      .get(`/purchase-orders/lines/${pid}/import-history`)
      .then((res) => {
        if (!cancelled) setImportHistory(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setImportHistory([]);
      })
      .finally(() => {
        if (!cancelled) setImportHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pid, hasPerm]);

  const handleBack = () => {
    navigate("/licence-control/purchase-orders");
  };

  const handleCancel = () => {
    navigate("/licence-control/purchase-orders");
  };

  const handleSave = async () => {
    if (!pid) return;

    // LWI-537 — detect admin transition of authorizationStatus back to
    // Outstanding on a line that actually has authorization data or
    // attachments worth warning about. Show a confirm and, if accepted,
    // include the resetAuthorization flag so the backend wipes those
    // fields + attachments inside a transaction.
    const initialAuth = initialAuthRef.current;
    const wasOutstanding = initialAuth.authorizationStatus === "Outstanding";
    const isNowOutstanding = formData.authorizationStatus === "Outstanding";
    const anyAuthRow = authorizationRows.some(
      (r) => !!(r.licenseNumber || "").trim() || !!(r.authorizationLine || "").trim()
    );
    const hadAuthData =
      !!initialAuth.authorizationNumber ||
      !!initialAuth.authorizationLine ||
      initialAuth.authorizedToShip === true ||
      anyAuthRow ||
      certAttachments.length > 0;
    const isReset = isAdmin && !wasOutstanding && isNowOutstanding && hadAuthData;

    if (isReset) {
      const proceed = await confirm({
        title: "Reset this PO line to Outstanding?",
        description:
          // LWI-909 — confirm copy lists comments now that the reset also
          // wipes Supplier Comments alongside the authorization fields.
          "Authorization number, authorization line, 'authorized to ship', supplier comments, and all uploaded attachments will be cleared. The supplier will need to re-submit. This cannot be undone.",
        confirmLabel: "Reset",
        cancelLabel: "Cancel",
      });
      if (!proceed) return;
    }

    setSaving(true);
    setSaveSuccess(false);
    setSuccessMessage(null);
    setError(null);
    try {
      // LWI-535 — authoritative multi-license payload. Backend replaces
      // po_line_licenses for this pid from this array and syncs the scalar
      // authorization_number/authorization_line on po_lines to mirror row 0.
      const licensesPayload = authorizationRows
        .map((r) => ({
          number: (r.licenseNumber || "").trim(),
          line: (r.authorizationLine || "").trim(),
        }))
        .filter((r) => r.number !== "" || r.line !== "");

      const putResponse = await apiClient.put(`/purchase-orders/lines/${pid}`, {
        status: formData.poLineStatus || null,
        needByDate: formData.needByDate || null,
        promiseDate: formData.promiseDate || null,
        shipToLocation: formData.shipToLocation || null,
        releaseNumber: formData.releaseNo || null,
        shipmentNumber: formData.shipmentNo || null,
        distribution: formData.distribution || null,
        project: formData.project || null,
        programEndUse: formData.programEndUse || null,
        usml: formData.usml || null,
        eccn: formData.eccn || null,
        authorizationRequirement: formData.authorizationRequirement || null,
        authorizationStatus: formData.authorizationStatus || null,
        comments: formData.comments || null,
        notes: formData.notes || null,
        // LWI-704 / LWI-734 — admin-only on the BE; suppliers' payload is
        // silently dropped by UpdatePurchaseOrderLine so it's safe to send
        // either way and keeps the request shape stable across roles.
        adminComments: formData.adminComments || null,
        hasUsContent: formData.hasUsContent,
        licenses: licensesPayload,
        ...(isReset ? { resetAuthorization: true } : {}),
      });

      // LWI-784 — BE returns a `warnings` object alongside `success:true`
      // when a part of the payload was silently ignored (e.g. an admin
      // sent adminComments but the perm gate dropped it). Surface those
      // so the user doesn't see "saved!" while half the form was thrown
      // away on the server.
      // LWI-809 — only show these warnings to admins. Suppliers can't see
      // or edit the admin_comments field; the "Admin Comments was not
      // saved" warning was confusing them (their save actually succeeded
      // and the dropped adminComments=null in the payload is harmless).
      // LWI-870 — but `cci_license_locked` IS supplier-facing: it explains
      // why their license edits did not persist on a save that otherwise
      // succeeded, so show it to suppliers too.
      const warnings = putResponse?.data?.warnings;
      if (warnings && Object.keys(warnings).length > 0) {
        const supplierVisibleKeys = new Set(["cci_license_locked"]);
        const linesToShow = isAdmin
          ? Object.values(warnings).filter((v) => typeof v === "string")
          : Object.entries(warnings)
              .filter(([k, v]) => supplierVisibleKeys.has(k) && typeof v === "string")
              .map(([, v]) => v);
        if (linesToShow.length > 0) {
          setError(linesToShow.join(" "));
        }
      }
      // LWI-715 — re-derive the supplier-edit lock from the freshly-persisted
      // has_us_content. A reset clears the field on the server, so the ref
      // flips back to false; otherwise it tracks the value the client just
      // asked the server to save. Updating the ref here (instead of doing a
      // full refetch) avoids the loading-spinner flash that fetchPO would
      // cause and matches the existing optimistic-state pattern in the
      // isReset branch below.
      originalHasUsContentRef.current = isReset ? false : formData.hasUsContent === true;
      // If we're transitioning hasUsContent to false, the backend's transition
      // rule (LWI-517) will have flipped authorized_to_ship to 1. Reflect that
      // locally so the chip stays green after the save succeeds.
      if (formData.hasUsContent === false) {
        setSavedAuthorizedToShip(true);
      }
      if (isReset) {
        // Clear the fields the server just wiped so the UI matches the new
        // truth without needing a page reload.
        // LWI-914 — also clear has_us_content locally (BE sets it to NULL on
        // reset) and stomp any bulk-apply dialog state so a reset can't leave
        // a residual pop-up behind on the rare path where the page doesn't
        // navigate away after save.
        // LWI-909 — `comments` (Supplier Comments) joined the reset wipe.
        setFormData((f) => ({
          ...f,
          authorizationNumber: "",
          authorizationLine: "",
          hasUsContent: null,
          comments: "",
        }));
        setAuthorizationRows([]);
        setCertAttachments([]);
        setSavedAuthorizedToShip(false);
        setBulkDialogMode(null);
        setBulkCandidates([]);
        navigateAfterBulkCloseRef.current = false;
        initialAuthRef.current = {
          authorizationStatus: "Outstanding",
          authorizationNumber: "",
          authorizationLine: "",
          authorizedToShip: false,
        };
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      // LWI-557 — after a successful save with hasUsContent === true, look
      // for OTHER open POs of this supplier with the same part + program
      // where the supplier hasn't answered yet. If any exist, open the
      // bulk-apply modal. If none, the save completes silently as before.
      //
      // LWI-884 — this pop-up is supplier-only. Admins editing a PO to
      // review/add comments don't propagate a supplier's "Yes" answer
      // across sibling POs; that's the supplier's action. Rita reported
      // seeing this dialog after a routine review save — bypass it for
      // anyone holding `lcs_po.view.any` (the admin gate).
      let bulkDialogOpened = false;
      if (formData.hasUsContent === true && !isAdmin) {
        try {
          const candidatesRes = await apiClient.get(
            `/purchase-orders/lines/${pid}/us-content-candidates`
          );
          const candidates = candidatesRes.data?.data || [];
          if (candidates.length > 0) {
            setBulkCandidates(candidates);
            setBulkDialogMode("us_content");
            bulkDialogOpened = true;
          }
        } catch (candidatesErr) {
          // Best-effort — log and skip; the source save already succeeded.
          console.warn("Failed to fetch US-content candidates:", candidatesErr);
        }
      }

      // LWI-823 — after a successful save, return the user to the PO list
      // so they can't accidentally re-save the same line (the prior UX let
      // suppliers loop through Save indefinitely without realising the
      // change was already persisted). If a bulk dialog opened we defer
      // the navigation until that dialog closes (see handleBulkClose /
      // handleBulkConfirm). Snackbar carries the success message across
      // the page transition.
      if (bulkDialogOpened) {
        navigateAfterBulkCloseRef.current = true;
      } else {
        showSnackbar("PO line saved", "success");
        navigate("/licence-control/purchase-orders");
      }
    } catch (err) {
      console.error("Error saving PO line:", err);
      setError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // LWI-534 — filter the license Autocomplete to licenses whose program_name
  // matches the PO's program (resolved server-side via
  // po_master/po_lines.project -> lcs_projects -> lcs_programs). Admins
  // bypass the filter so they can assign any license. If the PO's project is
  // not mapped to a program (projectProgramName === null), the filter is off
  // so the user isn't stranded with an empty dropdown.
  const filteredLicenseNumbers = useMemo(() => {
    if (isAdmin || !projectProgramName) return licenseNumbers;
    const allowedByNumber = new Set(
      licenses
        .filter((l) => l.programName === projectProgramName)
        .map((l) => l.licenseNumber)
        .filter(Boolean)
    );
    return licenseNumbers.filter((n) => allowedByNumber.has(n));
  }, [licenses, licenseNumbers, projectProgramName, isAdmin]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Admin-only: flip authorized_to_ship to true on the backend immediately
  // (no Save button round-trip). Backend enforces the admin-role check.
  //
  // LWI-558 — after a successful single authorize on the source PO, look
  // for OTHER open POs awaiting authorization with the same part + program.
  // If any exist, open the bulk-authorize modal. Source is always
  // authorized regardless of what the admin does in the modal.
  const handleAuthorizeShipping = async () => {
    if (!pid) return;
    try {
      // LWI-977 — send the current Admin Comments value alongside the
      // authorize so the source row is fresh in the DB before the
      // subsequent bulk-authorize-shipping endpoint reads it. Without
      // this, an admin who types into Admin Comments and clicks
      // Authorize for Shipping (skipping Save) loses the typed text:
      // BE only flipped authorized_to_ship, then the bulk-authorize
      // copied the *stale* admin_comments to every selected related PO
      // (Rita / Pam UAT report on PO 35705900 vs 35711502 / 35717520).
      // BE drops the field for non-admin callers (same gate as
      // UpdatePurchaseOrderLine), so sending it unconditionally is
      // safe.
      await apiClient.post(`/purchase-orders/lines/${pid}/authorize-shipping`, {
        adminComments: formData.adminComments || null,
      });
      setSavedAuthorizedToShip(true);
      setExplicitlyNotAuthorized(false); // LWI-1169
      setSuccessMessage(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      try {
        const candidatesRes = await apiClient.get(
          `/purchase-orders/lines/${pid}/authorization-candidates`
        );
        const candidates = candidatesRes.data?.data || [];
        if (candidates.length > 0) {
          setBulkCandidates(candidates);
          setBulkDialogMode("authorization");
        }
      } catch (candidatesErr) {
        // Best-effort — log and skip; source authorize already succeeded.
        console.warn(
          "Failed to fetch authorization candidates:",
          candidatesErr
        );
      }
    } catch (err) {
      console.error("Failed to authorize shipping:", err);
      setError(
        err.response?.data?.message ||
          "Failed to authorize shipping. Please try again."
      );
    }
  };

  // LWI-1169 — Admin-only: non-destructive revoke. Flips authorized_to_ship
  // back to 0 ("Not Authorized to ship") without wiping licenses / comments /
  // attachments (that's the separate destructive reset). Handles Rita's case:
  // a CDN/International PO auto-authorized on a "No US content" answer and was
  // mistakenly left Authorized with no clean way to undo it.
  const handleRevokeShipping = async () => {
    if (!pid) return;
    try {
      const res = await apiClient.post(
        `/purchase-orders/lines/${pid}/revoke-shipping`,
        {}
      );
      setSavedAuthorizedToShip(false);
      setExplicitlyNotAuthorized(true);
      // LWI-1207 — the backend also clears the US-content answer and supplier
      // comments on revoke; mirror that in the form so the US content Yes/No
      // resets (nothing highlighted) and the Supplier Comments box empties.
      setFormData((prev) => ({ ...prev, hasUsContent: null, comments: "" }));
      // Backend returns a warning when a finalized CCI references this line.
      // Keep that message persistent (manual dismiss); auto-clear otherwise.
      const warning = res.data?.warning;
      setSuccessMessage(warning || null);
      setSaveSuccess(true);
      if (!warning) setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to revoke shipping authorization:", err);
      setError(
        err.response?.data?.message ||
          "Failed to set Not Authorized. Please try again."
      );
    }
  };

  /**
   * LWI-557 / LWI-558 — confirm handler for the shared bulk action dialog.
   * Routes to the right backend endpoint based on the active mode and shows
   * an inline success Alert with the applied count. Does not throw on
   * partially-skipped responses — those still count as "successful" from
   * the user's perspective; the snackbar/Alert reports both numbers.
   */
  const handleBulkConfirm = async (targetPids) => {
    if (!pid || !bulkDialogMode || targetPids.length === 0) return;

    const endpoint =
      bulkDialogMode === "us_content"
        ? `/purchase-orders/lines/${pid}/bulk-us-content`
        : `/purchase-orders/lines/${pid}/bulk-authorize-shipping`;

    const response = await apiClient.post(endpoint, { target_pids: targetPids });
    const applied = Number(response?.data?.applied || 0);
    const skipped = Array.isArray(response?.data?.skipped)
      ? response.data.skipped.length
      : 0;

    const noun = bulkDialogMode === "us_content"
      ? "PO line(s) had your US content answer applied"
      : "PO line(s) authorized to ship";
    let msg = `${applied} ${noun}.`;
    if (skipped > 0) {
      msg += ` ${skipped} skipped (no longer eligible).`;
    }
    // LWI-821 — bulk apply success is persistent. The supplier needs to
    // see what just happened (which lines were applied, how many were
    // skipped) without it disappearing in ~5s. Manual dismissal only.
    setSuccessMessage(msg);
    setSaveSuccess(true);

    // Close the dialog after success.
    setBulkDialogMode(null);
    setBulkCandidates([]);

    // LWI-823 — if this dialog was spawned by a Save, return the user to
    // the PO list now that the bulk-apply leg is done. Snackbar carries
    // the success across the navigation.
    if (navigateAfterBulkCloseRef.current) {
      navigateAfterBulkCloseRef.current = false;
      showSnackbar(msg, "success");
      navigate("/licence-control/purchase-orders");
    }
  };

  const handleBulkClose = () => {
    setBulkDialogMode(null);
    setBulkCandidates([]);
    // LWI-823 — same deferred navigation when the supplier closes the
    // dialog without applying. The source line was still saved, so the
    // PO-list return matches the no-bulk-dialog path.
    if (navigateAfterBulkCloseRef.current) {
      navigateAfterBulkCloseRef.current = false;
      showSnackbar("PO line saved", "success");
      navigate("/licence-control/purchase-orders");
    }
  };

  // Download the US Content certification Excel template (LWI-518)
  const handleCertDownloadTemplate = async () => {
    try {
      const response = await apiClient.get(
        "/purchase-orders/us-content-template",
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "us_content_certification.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // 404 is expected when the tenant hasn't provisioned the template file —
      // the user already sees a friendly message. Keep it out of RUM by using
      // console.warn (RUM captures console.error, not console.warn).
      if (err.response?.status === 404) {
        console.warn("Template download failed:", err);
        setError(
          "Certification template is not available. Please contact an administrator."
        );
      } else {
        console.error("Template download failed:", err);
        setError("Failed to download certification template.");
      }
    }
  };

  // Upload one or more certification files to this PO line
  const handleCertUpload = async (files) => {
    if (!pid || !files || files.length === 0) return;

    // Optimistic placeholder rows so the user sees upload progress.
    // uploadedAt is set client-side here and carried forward after the POST
    // resolves; the next page load gets the authoritative server timestamp.
    const tempRows = files.map((f, i) => ({
      id: `temp-${Date.now()}-${i}`,
      name: f.name,
      size: f.size,
      type: f.type,
      uploadedAt: new Date().toISOString(),
      isUploading: true,
    }));
    setCertAttachments((prev) => [...prev, ...tempRows]);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const tempId = tempRows[i].id;
      try {
        const formData = new FormData();
        formData.append("file", file);
        const resp = await apiClient.post(
          `/purchase-orders/lines/${pid}/attachments`,
          formData
        );
        const att = resp.data?.data;
        setCertAttachments((prev) =>
          prev.map((a) =>
            a.id === tempId
              ? {
                  id: att.id,
                  name: att.original_name,
                  size: att.size_bytes,
                  type: att.mime_type,
                  uploadedAt: a.uploadedAt,
                }
              : a
          )
        );
      } catch (err) {
        console.error("Certification upload failed:", err);
        setCertAttachments((prev) => prev.filter((a) => a.id !== tempId));
        setError(
          err.response?.data?.message ||
            `Failed to upload ${file.name}.`
        );
      }
    }
  };

  const handleCertDownload = async (id, filename) => {
    try {
      const response = await apiClient.get(
        `/purchase-orders/lines/${pid}/attachments/${id}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "attachment";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Attachment download failed:", err);
      setError("Failed to download attachment.");
    }
  };

  const handleCertRemove = async (id) => {
    if (typeof id === "string" && id.startsWith("temp-")) {
      setCertAttachments((prev) => prev.filter((a) => a.id !== id));
      return;
    }
    const target = certAttachments.find((a) => a.id === id);
    const proceed = await confirm({
      title: "Delete attachment?",
      description: target?.name
        ? `"${target.name}" will be permanently removed. This cannot be undone.`
        : "This attachment will be permanently removed. This cannot be undone.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
    });
    if (!proceed) return;
    try {
      await apiClient.delete(
        `/purchase-orders/lines/${pid}/attachments/${id}`
      );
      setCertAttachments((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error("Attachment delete failed:", err);
      setError(
        err.response?.data?.message || "Failed to delete attachment."
      );
    }
  };

  const sharedInputProps = (testId) => ({
    variant: "standard",
    fullWidth: true,
    "data-testid": testId,
    slotProps: {
      inputLabel: {
        sx: { fontSize: fontSizes.sm, letterSpacing: letterSpacings.caption, color: "text.primary" },
      },
      input: {
        sx: {
          fontSize: fontSizes.base,
          letterSpacing: letterSpacings.body1,
          lineHeight: lineHeights.md,
          color: "text.primary",
        },
      },
    },
  });

  const readonlyProps = (testId) => ({
    variant: "standard",
    fullWidth: true,
    "data-testid": testId,
    slotProps: {
      inputLabel: {
        sx: { fontSize: fontSizes.sm, letterSpacing: letterSpacings.caption, color: "text.primary" },
      },
      input: {
        readOnly: true,
        sx: {
          fontSize: fontSizes.base,
          letterSpacing: letterSpacings.body1,
          lineHeight: lineHeights.md,
          color: "text.primary",
          bgcolor: "action.hover",
          borderRadius: 1,
          px: 1,
        },
      },
    },
  });

  const sectionTitle = (title, testId) => (
    <Typography
      data-testid={testId}
      sx={{ fontSize: fontSizes.lg, fontWeight: 600, color: "text.primary", mb: 1 }}
    >
      {title}
    </Typography>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      data-testid="edit-purchase-order-page"
      sx={{ maxWidth: 1520, mx: "auto" }}
    >
      {/* Page Header */}
      <Box
        data-testid="edit-po-header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 3,
          borderBottom: 1, borderColor: "divider",
          mb: 5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={handleBack}
            data-testid="back-button"
            sx={{ color: "text.primary", ml: -1 }}
          >
            <ArrowBack sx={{ fontSize: fontSizes["3xl"] }} />
          </IconButton>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontSize: fontSizes["3xl"],
                  fontWeight: 600,
                  lineHeight: lineHeights.xl,
                  color: "text.primary",
                }}
              >
                Edit PO {basePONo}
              </Typography>
              {formData.lineNo && (
                <Typography
                  sx={{ fontSize: fontSizes.lg, color: "text.secondary", ml: 1 }}
                >
                  — Line {formData.lineNo}
                  {formData.shipmentNo ? ` · Shipment ${formData.shipmentNo}` : ""}
                </Typography>
              )}
            </Box>
            {/* LWI-842 — source provenance: when the Oracle feed wrote the
                row and which file it came from. Both null for IPR-imported
                POs; suppress the line when neither value is present. */}
            {(formData.oracleCreateDate || formData.filename) && (
              <Typography
                data-testid="po-provenance"
                sx={{ fontSize: fontSizes.sm, color: "text.secondary", mt: 0.25 }}
              >
                {formData.oracleCreateDate && (
                  <>Oracle Created: {new Date(formData.oracleCreateDate).toLocaleString()}</>
                )}
                {formData.oracleCreateDate && formData.filename && " · "}
                {formData.filename && <>File: {formData.filename}</>}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            data-testid="cancel-button"
            sx={{
              textTransform: "none",
              borderColor: "primary.main",
              color: "primary.main",
              px: 2,
              py: 0.75,
              fontSize: fontSizes.base,
              fontWeight: 600,
              letterSpacing: letterSpacings.button,
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: (theme) =>
                  alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isLocked || saving || !pid || (hasPerm("lcs_po.view_comments") && formData.hasUsContent === true && !formData.comments?.trim())}
            data-testid="save-button"
            sx={{
              textTransform: "none",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              px: 2,
              py: 0.75,
              fontSize: fontSizes.base,
              fontWeight: 600,
              letterSpacing: letterSpacings.button,
              boxShadow: shadow.panel,
              "&:hover": { bgcolor: "primary.main" },
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
          </Button>
        </Box>
      </Box>

      {/* LWI-1150 — Details/Audit tabs (audit-permission holders only). */}
      {canViewAudit && (
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          sx={{ mb: 3 }}
        >
          <Tab label="Details" value="details" data-testid="po-tab-details" />
          <Tab label="Audit" value="audit" data-testid="po-tab-audit" />
        </Tabs>
      )}

      {activeTab === "audit" && canViewAudit ? (
        <PoLineAuditTable
          pid={pid}
          lineRef={
            formData.lineNo
              ? [basePONo, formData.releaseNo || "", formData.lineNo, formData.shipmentNo || "", formData.distribution || ""].join(",")
              : null
          }
        />
      ) : (
        <>
      {/* LWI-800 — AB03 / Sumner early-warning banner. The PO is destined
          for a US-to-US delivery point (CAN-AB1 / CAN-AB3); CCI submission
          will be auto-suspended at the end of the workflow (LWI-768,
          LWI-799). Surface this here so the supplier knows up-front and
          can contact the buyer before completing the wizard.
          LWI-1102 — US suppliers only; a CDN/INT supplier shipping into
          Sumner is a legitimate cross-border CCI. */}
      {isUsToUsDestination && isUsaSupplier && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This PO is destined for a US-to-US delivery point (Sumner — {destinationCode}).
          It cannot be submitted through the CCI flow. Please contact your
          GDLS buyer to re-route the shipment or use the appropriate
          domestic shipping process.
        </Alert>
      )}

      {/* Status messages */}
      {/* LWI-715 / LWI-763 / LWI-787 — supplier-edit lock banner. Shown when
          the line has been Authorized to Ship and the caller has no bypass
          permission. Wording matches the actual gate (was previously stale
          "marked as containing US content" from the LWI-715 era). */}
      {isLocked && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This line is locked because it has been authorized to ship.
          Contact a compliance administrator to make changes.
        </Alert>
      )}
      {/* LWI-870 — license-only lock when an APPROVED/SUSPENDED CCI carries
          this line's license. Distinct from the broader authorized-to-ship
          lock above; only suppressed when that one is already showing. */}
      {!isLocked && licenseFieldsLocked && (
        <Alert severity="warning" sx={{ mb: 3 }} data-testid="cci-locked-license-alert">
          License and Authorization fields are read-only on this line —
          a CCI referencing this PO has been submitted or approved.
          Contact a compliance administrator to make license changes.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saveSuccess && (
        // LWI-821 — dismissible so the persistent bulk-apply confirmation
        // can be cleared manually. The non-bulk save path still auto-clears
        // after 3s (existing setTimeout in handleSave) so this onClose is
        // primarily for the bulk-apply Alert.
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => {
            setSaveSuccess(false);
            setSuccessMessage(null);
          }}
        >
          {successMessage || "Changes saved successfully."}
        </Alert>
      )}

      {/* LWI-557 / LWI-558 — shared bulk action dialog */}
      <RelatedPosBulkActionDialog
        open={bulkDialogMode !== null}
        onClose={handleBulkClose}
        candidates={bulkCandidates}
        onConfirm={handleBulkConfirm}
        title={
          bulkDialogMode === "us_content"
            ? "Apply your US content answer to these POs"
            : "Authorize these POs to ship"
        }
        description={
          bulkDialogMode === "us_content"
            ? "Other open POs share this part number and program. Selecting any of them will copy your Yes answer, comments, and uploaded attachments to those POs as well. Each will then be awaiting admin authorization."
            : "Other open POs awaiting authorization share this part number and program. Selecting any of them will authorize them to ship and copy this PO's attachments onto each."
        }
        confirmLabel={
          bulkDialogMode === "us_content" ? "Apply" : "Authorize all"
        }
      />

      {/* Single-page form */}
      <Box
        data-testid="po-form-container"
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: shadow.panel,
          p: 4,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {/* Purchase Order Section */}
        <Box data-testid="po-section-purchase-order">
          {sectionTitle("Purchase Order", "po-section-title")}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 3,
            }}
          >
            <TextField
              label="PO No"
              value={formData.poNo}
              {...readonlyProps("po-no-field")}
            />
            <TextField
              label="Release No"
              value={formData.releaseNo}
              onChange={(e) => handleFormChange("releaseNo", e.target.value)}
              // LWI-787 — lock supplier-editable inputs once the line is
              // Authorized to Ship. The backend already 403s the save in
              // that state; this stops the UI from lying about it.
              // Suppliers also can't edit these PO-identity / classification
              // fields — they come from Oracle and shouldn't be overridden.
              disabled={isLocked || supplierReadOnly}
              {...sharedInputProps("release-no-field")}
            />
            <TextField
              label="Line No"
              value={formData.lineNo}
              {...readonlyProps("line-no-field")}
            />
            <TextField
              label="Shipment No"
              value={formData.shipmentNo}
              onChange={(e) => handleFormChange("shipmentNo", e.target.value)}
              disabled={isLocked || supplierReadOnly}
              {...sharedInputProps("shipment-no-field")}
            />
            <TextField
              label="Distribution"
              value={formData.distribution}
              onChange={(e) => handleFormChange("distribution", e.target.value)}
              disabled={isLocked || supplierReadOnly}
              {...sharedInputProps("distribution-field")}
            />
          </Box>
        </Box>

        <Divider />

        {/* Item Section */}
        <Box data-testid="po-section-item">
          {sectionTitle("Item", "item-section-title")}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              label="Item"
              value={formData.item}
              {...readonlyProps("item-field")}
            />
            <TextField
              label="Item Description"
              value={formData.itemDescription}
              {...readonlyProps("item-description-field")}
            />
            <TextField
              label="Quantity"
              value={formData.quantity}
              {...readonlyProps("quantity-field")}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 3,
            }}
          >
            <TextField
              label="Quantity Shipped"
              value={formData.quantityShipped}
              {...readonlyProps("quantity-shipped-field")}
            />
            <TextField
              label="Unit Value"
              value={formData.unitValue}
              {...readonlyProps("unit-value-field")}
            />
            {/* LWI-854 — currency code is gated on lcs_po.view_currency.
                Merge the endAdornment into readonlyProps's slotProps.input so
                we keep the readOnly + styling baked in by the helper. */}
            {(() => {
              const baseProps = readonlyProps("extended-value-field");
              const showCurrency =
                hasPerm("lcs_po.view_currency") && formData.monetaryCurrency;
              const inputSlot = showCurrency
                ? {
                    ...baseProps.slotProps.input,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontWeight: 500 }}
                          data-testid="extended-value-currency"
                        >
                          {formData.monetaryCurrency}
                        </Typography>
                      </InputAdornment>
                    ),
                  }
                : baseProps.slotProps.input;
              return (
                <TextField
                  label="Extended Value"
                  value={
                    formData.unitValue && formData.quantity
                      ? (parseFloat(formData.unitValue) * parseFloat(formData.quantity)).toFixed(2)
                      : formData.extendedValue
                  }
                  {...baseProps}
                  slotProps={{ ...baseProps.slotProps, input: inputSlot }}
                />
              );
            })()}
          </Box>
        </Box>

        <Divider />

        {/* Delivery Section */}
        <Box data-testid="po-section-delivery">
          {sectionTitle("Delivery", "delivery-section-title")}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 3,
            }}
          >
            <TextField
              label="Oracle Status"
              value={formData.poLineStatus}
              disabled
              {...sharedInputProps("po-line-status-field")}
              sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: (theme) => theme.palette.text.primary } }}
            />
            <TextField
              label="Ship To Location"
              value={formData.shipToLocation}
              onChange={(e) => handleFormChange("shipToLocation", e.target.value)}
              disabled={isLocked || supplierReadOnly} // LWI-787 + supplier lockout
              {...sharedInputProps("ship-to-location-field")}
            />
            <TextField
              label="Need By Date"
              type="date"
              value={formData.needByDate}
              onChange={(e) => handleFormChange("needByDate", e.target.value)}
              disabled={isLocked || supplierReadOnly} // LWI-787, LWI-824
              slotProps={{
                inputLabel: { shrink: true, sx: { fontSize: fontSizes.sm } },
                input: { sx: { fontSize: fontSizes.base } },
              }}
              variant="standard"
              fullWidth
              data-testid="need-by-date-field"
            />
            <TextField
              label="Promise Date"
              type="date"
              value={formData.promiseDate}
              onChange={(e) => handleFormChange("promiseDate", e.target.value)}
              disabled={isLocked || supplierReadOnly} // LWI-787, LWI-824
              slotProps={{
                inputLabel: { shrink: true, sx: { fontSize: fontSizes.sm } },
                input: { sx: { fontSize: fontSizes.base } },
              }}
              variant="standard"
              fullWidth
              data-testid="promise-date-field"
            />
          </Box>
        </Box>

        <Divider />

        {/* Supplier Section */}
        <Box data-testid="po-section-supplier">
          {sectionTitle("Supplier", "supplier-section-title")}
          <Box
            sx={{
              display: "grid",
              // LWI-560 — widen to 6 columns for internal users (admin/owner)
              // so the read-only Task field can sit alongside Project.
              // Suppliers see 5 columns and no Task field.
              gridTemplateColumns: `repeat(${isInternal ? 6 : 5}, 1fr)`,
              gap: 3,
            }}
          >
            <TextField
              label="Supplier No"
              value={formData.supplierNo}
              {...readonlyProps("supplier-no-field")}
            />
            <TextField
              label="Supplier Name"
              value={formData.supplierName}
              {...readonlyProps("supplier-name-field")}
            />
            <TextField
              label="Buyer"
              value={formData.buyer}
              {...readonlyProps("buyer-field")}
            />
            <Autocomplete
              options={programOptions}
              value={formData.programEndUse || null}
              onChange={(_e, newValue) =>
                handleFormChange("programEndUse", newValue || "")
              }
              disabled={isLocked || supplierReadOnly} // LWI-787, LWI-824
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Program-End Use"
                  variant="standard"
                  fullWidth
                  data-testid="program-end-use-field"
                  slotProps={{
                    inputLabel: {
                      sx: { fontSize: fontSizes.sm, letterSpacing: letterSpacings.caption, color: "text.primary" },
                    },
                  }}
                />
              )}
            />
            <TextField
              label="Project"
              value={formData.project}
              onChange={(e) => handleFormChange("project", e.target.value)}
              disabled={isLocked || supplierReadOnly} // LWI-787, LWI-824
              {...sharedInputProps("project-field")}
            />
            {isInternal && (
              // LWI-560 — internal-only (admin or owner) read-only Task field,
              // sourced from po_master.task. Suppliers do not see this. Pulls
              // from the backend via po.task on the GetPurchaseOrder response
              // (see iq_api LWI-560 backend PR).
              <TextField
                label="Task"
                value={formData.task}
                {...readonlyProps("task-field")}
              />
            )}
          </Box>
        </Box>

        <Divider />

        {/* Classification Section */}
        <Box data-testid="po-section-license">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 3,
            }}
          >
            <TextField
              label="ECCN"
              value={formData.eccn}
              onChange={(e) => handleFormChange("eccn", e.target.value)}
              disabled={isLocked || supplierReadOnly} // LWI-787 + supplier lockout
              {...sharedInputProps("eccn-field")}
            />
            <TextField
              label="USML"
              value={formData.usml}
              onChange={(e) => handleFormChange("usml", e.target.value)}
              disabled={isLocked || supplierReadOnly} // LWI-787 + supplier lockout
              {...sharedInputProps("usml-field")}
            />
            <TextField
              label="Authorization Requirement"
              value={formData.authorizationRequirement}
              disabled
              {...sharedInputProps("authorization-requirement-field")}
              sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: (theme) => theme.palette.text.primary } }}
            />
          </Box>
        </Box>

        {hasPerm("lcs_po.view_item_information") && <Divider />}

        {/* Item Information Section (from parts_master) — hidden from suppliers */}
        {hasPerm("lcs_po.view_item_information") && <Box data-testid="po-section-item-info">
          {sectionTitle("Item Information", "item-info-section-title")}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              label="ECL Goods"
              value={formData.eclGoods}
              {...readonlyProps("ecl-goods-field")}
            />
            <TextField
              label="ECCN EAR Technology"
              value={formData.eccnEarTechnology}
              {...readonlyProps("eccn-ear-technology-field")}
            />
            <TextField
              label="USML ITAR Technical Data"
              value={formData.usmlItarTechData}
              {...readonlyProps("usml-itar-tech-data-field")}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              label="ECL Technology"
              value={formData.eclTechnology}
              {...readonlyProps("ecl-technology-field")}
            />
            <TextField
              label="Canadian Controlled Goods"
              value={formData.canadianControlledGoods}
              {...readonlyProps("canadian-controlled-goods-field")}
            />
            <TextField
              label="US HS Code"
              value={formData.usHsCode}
              {...readonlyProps("us-hs-code-field")}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 3,
            }}
          >
            <TextField
              label="CA HS Code"
              value={formData.caHsCode}
              {...readonlyProps("ca-hs-code-field")}
            />
            <TextField
              label="Last Trade Compliance Review Date"
              value={formData.lastTradeComplianceReviewDate}
              {...readonlyProps("last-trade-compliance-review-date-field")}
            />
            <TextField
              label="ECR Compliance Review Date"
              value={formData.ecrComplianceReviewDate}
              {...readonlyProps("ecr-compliance-review-date-field")}
            />
          </Box>
        </Box>}

        {/* US Content Section — all non-USA suppliers (Canadian + international,
            LWI-1075) with authorization required. */}
        {isNonUsaSupplier &&
          formData.authorizationRequirement &&
          formData.authorizationRequirement !== "No License Required" && (
          <>
            <Divider />
            <Box data-testid="po-section-us-content">
              {sectionTitle("US Content", "us-content-section-title")}

              {/* LWI-517 — Authorized to Ship indicator.
                  Derived: green when the user has answered No (the backend's
                  transition rule will auto-authorize on save) OR the saved
                  server state already says authorized. Clicking Yes after No
                  reverts the chip to the saved state so what you see always
                  matches what would be persisted. */}
              {(() => {
                // LWI-1169 — an explicit server "Not Authorized" (0) wins over
                // the LWI-517 "No US content → will auto-authorize" preview, so
                // a revoked no-US-content line reads red instead of green.
                const isAuthorizedToShip =
                  savedAuthorizedToShip ||
                  (formData.hasUsContent === false && !explicitlyNotAuthorized);
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Chip
                      label={
                        isAuthorizedToShip
                          ? "Authorized to Ship"
                          : "Not Authorized to Ship"
                      }
                      color={isAuthorizedToShip ? "success" : "error"}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    {isAdmin && !isAuthorizedToShip && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleAuthorizeShipping}
                        sx={{ textTransform: "none" }}
                      >
                        Authorize for Shipping
                      </Button>
                    )}
                    {/* LWI-1169 — non-destructive revoke; shown only when the
                        server currently has this line authorized and the admin
                        holds the revoke permission. */}
                    {hasPerm("lcs_po.authorization.revoke") &&
                      savedAuthorizedToShip && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={handleRevokeShipping}
                          sx={{ textTransform: "none" }}
                        >
                          Set to Not Authorized
                        </Button>
                      )}
                    {!isAdmin &&
                      !isAuthorizedToShip &&
                      formData.hasUsContent === true && (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontStyle: "italic" }}
                        >
                          Pending GDLS review
                        </Typography>
                      )}
                  </Box>
                );
              })()}

              <Typography
                sx={{ fontSize: fontSizes.base, color: "text.primary", mb: 2 }}
              >
                Does this shipment contain US content?
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Button
                  variant={formData.hasUsContent === true ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => handleFormChange("hasUsContent", true)}
                  disabled={isLocked}
                  data-testid="us-content-yes"
                  sx={{ textTransform: "none", minWidth: 80 }}
                >
                  Yes
                </Button>
                <Button
                  variant={formData.hasUsContent === false ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => handleFormChange("hasUsContent", false)}
                  disabled={isLocked}
                  data-testid="us-content-no"
                  sx={{ textTransform: "none", minWidth: 80 }}
                >
                  No
                </Button>
              </Box>
              {formData.hasUsContent === true && (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Comments are required when US content is present.
                  </Alert>

                  <Typography
                    sx={{ fontSize: fontSizes.base, color: "text.primary", mb: 2 }}
                  >
                    Attach certifications —{" "}
                    <Typography
                      component="span"
                      onClick={handleCertDownloadTemplate}
                      data-testid="us-content-template-link"
                      sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontSize: fontSizes.base,
                        "&:hover": { color: "primary.dark" },
                      }}
                    >
                      download template here
                    </Typography>
                  </Typography>

                  {certAttachments.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <FileAttachmentList
                        attachments={certAttachments}
                        onRemove={handleCertRemove}
                        onDownload={handleCertDownload}
                        canDelete={isAdmin || !savedAuthorizedToShip}
                      />
                    </Box>
                  )}

                  {(isAdmin || !savedAuthorizedToShip) && (
                    <FileUploadZone
                      onFilesSelected={handleCertUpload}
                      accept=".xls,.xlsx,.csv,.pdf,.doc,.docx,.txt"
                    />
                  )}
                </>
              )}
            </Box>
          </>
        )}

        {isAdmin && <Divider />}

        {/* Supplier Comments / Admin Comments Section
            LWI-574 → LWI-704 / LWI-734 — comments restructure.

            po_lines.comments (this field) is "Supplier Comments": supplier
            edits; admin reads (and may also edit for correction). Backed
            by formData.comments so the LWI-574 shared-column behaviour is
            preserved. Label is the same for both roles now per LWI-734.

            po_lines.admin_comments (the next field) is admin-only edit but
            visible to suppliers — that's the LWI-704 "admin comments
            propagate to supplier" channel. Suppliers see it read-only.

            The separate admin-only Notes section further down (gated on
            lcs_po.view_notes) is unchanged; it's the private internal
            scratchpad and never shown to suppliers. */}
        <Box data-testid="po-section-supplier-comments">
          {sectionTitle("Supplier Comments", "supplier-comments-section-title")}
          <TextField
            label="Supplier Comments"
            value={formData.comments}
            onChange={(e) => handleFormChange("comments", e.target.value)}
            multiline
            rows={3}
            variant="outlined"
            fullWidth
            // LWI-763 follow-up — lock alongside the rest of the form
            // once the line is Authorized to Ship.
            disabled={isLocked}
            data-testid="comments-field"
            InputProps={{ sx: { fontSize: fontSizes.base } }}
            sx={{ mb: 3 }}
          />
        </Box>

        {/* Admin Comments — admin-only. LWI-704 follow-up: the section
            (label, field, and divider) is hidden from suppliers entirely
            so they don't see internal-only annotations. Admins still edit
            here; the supplier sees nothing in its place. */}
        {isAdmin && (
          <>
            <Divider />
            <Box data-testid="po-section-admin-comments" sx={{ mt: 3 }}>
              {sectionTitle("Admin Comments", "admin-comments-section-title")}
              <TextField
                label="Admin Comments"
                value={formData.adminComments}
                onChange={(e) => handleFormChange("adminComments", e.target.value)}
                multiline
                rows={3}
                variant="outlined"
                fullWidth
                data-testid="admin-comments-field"
                helperText="Internal-only — not visible to the supplier."
                InputProps={{ sx: { fontSize: fontSizes.base } }}
                sx={{ mb: 3 }}
              />
            </Box>
          </>
        )}

        <Divider />

        {/* Authorization Section.
            LWI-763 follow-up — every input here locks alongside the rest
            of the US Content section once the line is Authorized to Ship.
            Pam was able to type new values into the License # and Line #
            fields after Rita approved a US-content line; the BE 403'd the
            save (LWI-714 → LWI-763), but the inputs *looked* editable,
            which is misleading. The Authorization Status select, the
            license-row Autocompletes, the per-row remove button, and the
            Add License button all now respect `isLocked`. */}
        <Box data-testid="po-section-authorization">
          {sectionTitle("Authorization", "authorization-section-title")}
          <Box sx={{ mb: 2 }}>
            <TextField
              select
              label="Authorization Status"
              value={formData.authorizationStatus}
              onChange={(e) =>
                handleFormChange("authorizationStatus", e.target.value)
              }
              disabled={licenseFieldsLocked}
              {...sharedInputProps("authorization-status-field")}
              sx={{ maxWidth: 320 }}
            >
              <MenuItem value="">— None —</MenuItem>
              <MenuItem value="Applied For">Applied For</MenuItem>
              <MenuItem value="Outstanding">Outstanding</MenuItem>
              <MenuItem value="Assigned">Assigned</MenuItem>
              <MenuItem value="Not Required">Not Required</MenuItem>
            </TextField>
          </Box>
          {/* LWI-913 — license entry surface (Authorization # / Authorization
              Line # / Add License / Remove) is only shown when the user has
              transitioned the status to "Assigned". Outstanding / Applied For
              / Not Required / blank all hide the rows entirely. Existing
              persisted values stay in the DB until a reset (LWI-914) — they
              just become invisible while the status is non-Assigned. */}
          {formData.authorizationStatus === "Assigned" && (
          <>
          {/* LWI-535 — list of license rows. + adds, minus removes. */}
          {authorizationRows.map((row, index) => (
            <Box
              key={index}
              data-testid={`authorization-row-${index}`}
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 40px",
                gap: 3,
                alignItems: "flex-end",
                mb: 2,
              }}
            >
              <Autocomplete
                options={[
                  ...filteredLicenseNumbers.map((ln) => ({ label: ln, group: "Licenses" })),
                  ...exemptionOptions.map((ex) => ({ label: ex, group: "Exemptions" })),
                  // LWI-1164 — "Exceptions" subheader (RPL / LVS).
                  ...exceptionOptions.map((ex) => ({ label: ex, group: "Exceptions" })),
                ]}
                groupBy={(option) => option.group || ""}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.label || "")}
                isOptionEqualToValue={(option, value) => {
                  const optLabel = option.label || option;
                  if (optLabel === value) return true;
                  const normalize = (s) => String(s || "").replace(/^0+(\d)/, "$1");
                  return normalize(optLabel) === normalize(value);
                }}
                freeSolo
                value={row.licenseNumber || null}
                onChange={(_e, newValue) => {
                  const val = typeof newValue === "string" ? newValue : newValue?.label || "";
                  setAuthRowLicense(index, val);
                }}
                loading={licenseNumbersLoading}
                disabled={licenseNumbersLoading || licenseFieldsLocked}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={index === 0 ? "Authorization #" : ""}
                    variant="standard"
                    fullWidth
                    data-testid={`authorization-number-field-${index}`}
                    slotProps={{
                      inputLabel: {
                        sx: { fontSize: fontSizes.sm, letterSpacing: letterSpacings.caption, color: "text.primary" },
                      },
                    }}
                  />
                )}
              />
              <Autocomplete
                options={row.lineOptions}
                value={row.authorizationLine || null}
                onChange={(_e, newValue) => setAuthRowLine(index, newValue || "")}
                loading={row.linesLoading}
                disabled={row.linesLoading || !row.licenseNumber || licenseFieldsLocked}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={index === 0 ? "Authorization Line #" : ""}
                    variant="standard"
                    fullWidth
                    data-testid={`authorization-line-field-${index}`}
                    slotProps={{
                      inputLabel: {
                        sx: { fontSize: fontSizes.sm, letterSpacing: letterSpacings.caption, color: "text.primary" },
                      },
                    }}
                  />
                )}
              />
              <IconButton
                size="small"
                onClick={() => removeAuthRow(index)}
                disabled={licenseFieldsLocked}
                data-testid={`authorization-row-remove-${index}`}
                aria-label="Remove license row"
                sx={{ color: "grey.600", "&:hover": { color: "error.main" } }}
              >
                <RemoveCircleOutline fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Box sx={{ mt: 1 }}>
            <Button
              startIcon={<AddCircleOutline />}
              onClick={addAuthRow}
              disabled={licenseFieldsLocked}
              data-testid="authorization-row-add"
              sx={{ textTransform: "none" }}
            >
              Add License
            </Button>
          </Box>
          </>
          )}
        </Box>

        {hasPerm("lcs_po.view_notes") && <Divider />}

        {/* Notes Section — hidden from suppliers */}
        {hasPerm("lcs_po.view_notes") && <Box data-testid="po-section-notes">
          {sectionTitle("Notes", "notes-section-title")}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              label="Date / Timestamp"
              value={formData.notesDateTime}
              {...readonlyProps("notes-datetime-field")}
            />
          </Box>
          <TextField
            label="Notes"
            value={formData.notes}
            onChange={(e) => handleFormChange("notes", e.target.value)}
            multiline
            rows={3}
            variant="outlined"
            fullWidth
            data-testid="notes-field"
            InputProps={{ sx: { fontSize: fontSizes.base } }}
          />
        </Box>}

        {/* LWI-855 — Import History (admin-only). Renders nothing for users
            without `lcs_po.view_import_history`; BE enforces the gate too. */}
        {hasPerm("lcs_po.view_import_history") && <Divider />}
        {hasPerm("lcs_po.view_import_history") && (
          <Box data-testid="po-section-import-history">
            {sectionTitle("Import History", "import-history-section-title")}
            {importHistoryLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Loading import history…
                </Typography>
              </Box>
            ) : importHistory.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", py: 1 }}
                data-testid="import-history-empty"
              >
                No importer activity recorded for this PO line.
              </Typography>
            ) : (
              <Table size="small" data-testid="import-history-table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Filename</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 220 }}>Imported At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importHistory.map((row) => (
                    <TableRow key={row.id} data-testid={`import-history-row-${row.id}`}>
                      <TableCell sx={{ wordBreak: "break-all" }}>{row.filename}</TableCell>
                      <TableCell>
                        {row.seen_at ? new Date(row.seen_at).toLocaleString() : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </Box>
        </>
      )}
    </Box>
  );
};
